<template>
  <div class="app container-fluid px-0">
    <preloader-component></preloader-component>
    <router-view
      v-if="canViewPublicRoutes || canViewAuthenticatedRoutes"
    ></router-view>
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import Pusher from 'pusher-js';
import { $event } from '@/bus';
import { showDocumentProcessedNotification } from '@/utils/notifications';
import config from '@/config';

// components
import Preloader from '@/V2/Preloader.vue';

// constants
import { LOCAL_STORAGE_REDIRECT_URL } from '@/constants/login';
import {
  BASE_URL,
  ENV,
  isProduction,
} from '@inscribe/ui-kit/src/utils/environment';

// services
import ApiService from '@/shared/services/api.service';

const CONFIG = config[ENV];
export default {
  name: 'App',
  components: {
    'preloader-component': Preloader,
  },
  data() {
    return {
      message: null,
      success: null,
      broadcastChannel: null,
    };
  },
  computed: {
    ...mapState(['currentUser']),
    canViewPublicRoutes() {
      return this.$route.path.startsWith('/auth') || this.$route.name === '404';
    },
    canViewAuthenticatedRoutes() {
      return (
        this.$store.state.hasUserCompletedRegistration &&
        !!this.$store.state.featureFlagsStore.featureFlags
      );
    },
  },
  mounted() {
    this.setupBroadcastChannel();

    const redirectUrl = localStorage.getItem(LOCAL_STORAGE_REDIRECT_URL);
    if (redirectUrl) {
      localStorage.removeItem(LOCAL_STORAGE_REDIRECT_URL);
      document.location = `https://${
        document.location.host
      }${document.location.pathname.replace(/^\//, '')}/${decodeURI(
        redirectUrl,
      )}`;
    }

    if (isProduction) {
      this.$intercom.boot();
    }

    ApiService.getAndSetCsrfToken()
      .then(() => this.getCurrentUser(CONFIG))
      .then((user) => {
        $event.$emit('preloader.off');
        this.maybeForceOtherTabRefresh(user.organisation.id);
        this.setupMostRecentWorkspace(user.organisation.id);
        this.connectToPusher(user, ApiService.token);
        const userObj = {
          id: user.uuid,
          email: user.email,
          organisation: user.organisation.name,
          screenSize: {
            height: window.innerHeight,
            width: window.innerWidth,
          },
        };

        if (isProduction) {
          Sentry.configureScope((scope) => {
            scope.setUser(userObj);
          });

          this.$intercom.update({
            user_id: user.id,
            name: `${user.first_name.split(' ').join('\xa0')} ${user.last_name
              .split(' ')
              .join('\xa0')}`,
            email: user.email,
            user_hash: user.intercom_hash,
            company: {
              name: user.organisation.name,
              id: user.organisation.id,
            },
          });
        }
        this.getTags();
        this.getWorkspaces();
      })
      .catch((error) => {
        $event.$emit('preloader.off');
        // eslint-disable-next-line no-console
        console.error(error);
      });

    $event.$on('document-state-change', (pusherData) => {
      if (
        this.$store.state.customers.selectedCustomer &&
        pusherData.customer_id ===
          this.$store.state.customers.selectedCustomer.id
      ) {
        this.getDocumentWithSignals({
          customerId: pusherData.customer_id,
          documentId: pusherData.document_id,
        }).then(({ data }) => {
          if (this.$store.state.currentUser.browser_notifications_enabled) {
            this.showDocumentNotification(data);
          }

          this.$store.commit('updateGeneric', {
            objectsName: 'documents',
            updatedObject: {
              ...document,
              ...data,
            },
          });
          if (data.is_fraudulent && this.$store.state.documentsMetadata) {
            const { fraudResults, pagination } =
              this.$store.state.documentsMetadata;
            this.$store.commit('setDocumentsMetadata', {
              pagination,
              fraudResults: {
                fraudulent_count: fraudResults.fraudulent_count + 1,
              },
            });
          }
        });
      } else if (this.$store.state.currentUser.browser_notifications_enabled) {
        this.getDocument({
          customerId: pusherData.customer_id,
          documentId: pusherData.document_id,
        }).then(({ data }) => {
          this.showDocumentNotification(data);
        });
      }
    });
    $event.$on('customer-fraud-rating-updated', (pusherData) => {
      if (
        this.$store.state.customers.selectedCustomer &&
        pusherData.customer_id ===
          this.$store.state.customers.selectedCustomer.id
      ) {
        this.$store.commit('updateSelectedCustomer', {
          fraud_rating: pusherData.fraud_rating,
        });
      }
    });
    $event.$on('document-review-agent-status-updated', (pusherData) => {
      if (
        this.$route.params.document_id === pusherData.document_id.toString()
      ) {
        this.fetchDocumentReviewAgentRunForDocId(pusherData.document_id);
      }
    });

    axios.interceptors.request.use(null, function (error) {
      return Promise.reject(error);
    });

    axios.interceptors.response.use(null, (error) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }
      const status = error?.response?.status;
      if (!document.location.hash.includes('/auth/') && status === 401) {
        const redirectUrl = encodeURI(document.location.hash);
        this.$router.push({ name: 'login', query: { redirectUrl } });
      }
      return Promise.reject(error);
    });
  },
  methods: {
    ...mapActions([
      'getWorkspaces',
      'getTags',
      'getCurrentUser',
      'getDocumentWithSignals',
      'getDocument',
      'fetchDocumentReviewAgentRunForDocId',
    ]),
    ...mapMutations(['setCurrentDocumentReviewAgentRun']),
    setupBroadcastChannel() {
      // Protect against older browsers without this API
      if ('BroadcastChannel' in window) {
        const channel = new BroadcastChannel('inscribe');

        channel.addEventListener('message', (event) => {
          const message = event.data;

          if (message.type === 'WORKSPACE_CHANGE') {
            // Workspace has changed elsewhere, need to reload the app to maintain a
            // consistent view of the data that can be loaded with the current session cookie
            window.location.href = '/';
          }
        });

        this.broadcastChannel = channel;
      }
    },
    maybeForceOtherTabRefresh(authenticatedOrganisationId) {
      if (this.broadcastChannel) {
        const previousId = localStorage.getItem('mostRecentWorkspace');
        if (previousId && previousId !== authenticatedOrganisationId) {
          // We've logged in to a different workspace than before
          // Inform other tabs running the app to reload
          this.broadcastChannel.postMessage({
            type: 'WORKSPACE_CHANGE',
            data: {},
          });
        }
      }
    },
    setupMostRecentWorkspace(organisationId) {
      localStorage.setItem('mostRecentWorkspace', organisationId);
    },
    connectToPusher(user, csrfToken) {
      Pusher.Runtime.createXHR = function () {
        const xhr = new XMLHttpRequest();
        xhr.withCredentials = true;
        return xhr;
      };
      const pusher = new Pusher(CONFIG.pusher.key, {
        cluster: CONFIG.pusher.cluster,
        forceTLS: true,
        authEndpoint: `${BASE_URL}api/v1/pusher/auth/`,
        authTransport: 'ajax',
        auth: {
          headers: {
            'X-CSRFToken': csrfToken,
          },
        },
      });
      const channel = pusher.subscribe(`private-inscribe-user_${user.uuid}`);
      channel.bind('document-state-change', function (data) {
        $event.$emit('document-state-change', data);
      });
      channel.bind('customer-fraud-rating-updated', function (data) {
        $event.$emit('customer-fraud-rating-updated', data);
      });
      channel.bind('document-review-agent-status-updated', function (data) {
        $event.$emit('document-review-agent-status-updated', data);
      });
    },
    showDocumentNotification(document) {
      showDocumentProcessedNotification(
        document.name,
        `/#/customer/${document.customer_id}/document/${document.id}`,
        document.state,
      );
    },
  },
};
</script>
<style scoped>
.app {
  height: 100%;
}
</style>
